import { Fragment, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useIsMutating, useIsFetching } from '@tanstack/react-query';

import { useDispatch, useSelector } from 'react-redux';

import { LoadingPartially } from '@jadwalh/ui';
import {
  getPartialLoadingState,
  startPartiallyLoading,
  stopPartiallyLoading,
} from '../store/partiallyLoading.slice';
import { reset, setProfile } from '../store/profile.slice';
import { getProfile } from '../api/profile.api';
import { SubscriptionUpgradeProvider } from '../hooks/useSubscriptionUpgrade';
import { removeAuthToken } from '../utils/auth-token';
import { resetIntegrationId } from '../store/zohoIntegrationId.slice';
import { setCurrentTenant } from '../store/activeTenant.slice';
import CalendarReauthBanner from './header/CalendarReauthBanner';

export default function Layout() {
  /* ------------------------------- Global State ------------------------------ */
  const dispatch = useDispatch();
  const isPartiallyLoading = useSelector(getPartialLoadingState);
  const location = useLocation();
  /* ------------------------------- Local State ------------------------------ */

  /* ---------------------------------- Hooks --------------------------------- */
  const activeQueries = useIsFetching({ stale: false });
  const activeMutations = useIsMutating();

  /* --------------------------------- Effects -------------------------------- */

  // Initialize profile data when layout loads
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isInvitationRoute = location.pathname.includes('/i/');
    
    if (token && !isInvitationRoute) {
      // Always fetch fresh profile data to avoid stale cached data
      getProfile().then((profile) => {
        // Explicitly dispatch to ensure Redux store is updated
        dispatch(setProfile(profile));
        
        const activeTenant = profile?.tenants?.data?.find(
          (tenant: any) => tenant.is_active === true
        );
        if (activeTenant) {
          dispatch(setCurrentTenant(activeTenant));
        }
      }).catch((error) => {
        console.error('Failed to fetch profile on layout mount:', error);
      });
    } else {
      if (!token) {
        // Clear profile data if no token
        dispatch(reset());
      }
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    activeQueries > 0 || activeMutations > 0
      ? dispatch(startPartiallyLoading())
      : dispatch(stopPartiallyLoading());
  }, [activeQueries, activeMutations]);

  // Check subscription status every 24 hours and logout if trial ended or suspended (for admin/member only)
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const isInvitationRoute = location.pathname.includes('/i/');
        
        if (!token || isInvitationRoute) {
          return;
        }

        // Fetch fresh profile data and ensure it's dispatched to Redux store
        const freshProfile = await getProfile();
        
        // Explicitly dispatch to ensure Redux store is updated with latest data
        dispatch(setProfile(freshProfile));
        
        const activeTenant = freshProfile?.tenants?.data?.find(
          (tenant: any) => tenant.is_active === true
        );
        if (activeTenant) {
          dispatch(setCurrentTenant(activeTenant));
        }
        
        const subscriptionInfo = freshProfile?.subscription_info;

        if (!subscriptionInfo) return;

        const statusValue = Number(subscriptionInfo?.status_value);
        const trialDaysRemaining = Number(subscriptionInfo?.trial_days_remaining ?? 0);
        
        // Check if trial ended (status is TRIAL and days remaining is 0 or less)
        const isTrialEnded = statusValue === 1 && trialDaysRemaining <= 0;
        
        // Check if status is SUSPENDED
        const isSuspended = statusValue === 6;

        // Check user role
        const roleValue = freshProfile?.role?.role_value;
        const isOwner = freshProfile?.tenants?.data?.[0]?.is_owner === true || roleValue === 1;
        const isAdmin = roleValue === 0;
        const isMember = roleValue === 2;

        // Check if profile status is BANNED (status === 3)
        const isBanned = Number(freshProfile?.status) === 3;

        // Logout if trial ended (all roles), if suspended (admin/member only, owner stays logged in), or if banned (admin/member only)
        const shouldLogout = isTrialEnded || (isSuspended && (isAdmin || isMember)) || (isBanned && (isAdmin || isMember));

        if (shouldLogout) {
          localStorage.removeItem('email');
          localStorage.removeItem('isNew');
          removeAuthToken();
          sessionStorage.clear();
          dispatch(resetIntegrationId());
          dispatch(reset());
          window.location.href = '/login';
        }
      } catch (error) {
        // Silently handle errors to avoid blocking the UI
        console.error('Error checking subscription status:', error);
        // Optionally, if API call fails due to auth, force logout
        if ((error as any)?.response?.status === 401) {
          const isInvitationRoute = location.pathname.includes('/i/');
          
          // Don't redirect if we're on invitation route
          if (!isInvitationRoute) {
            localStorage.removeItem('email');
            localStorage.removeItem('isNew');
            removeAuthToken();
            sessionStorage.clear();
            dispatch(resetIntegrationId());
            dispatch(reset());
            window.location.href = '/login';
          }
        }
      }
    };

    // Check immediately on mount
    checkSubscriptionStatus();

    // Set up interval to check every 24 hours
    const intervalId = setInterval(checkSubscriptionStatus, 86400000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [dispatch, location.pathname]);

  return (
    <SubscriptionUpgradeProvider>
      <Fragment>
        {/* {isPartiallyLoading && <LoadingPartially />} */}
        <Suspense fallback={<LoadingPartially />}>
          <CalendarReauthBanner />
          <Outlet />
        </Suspense>
      </Fragment>
    </SubscriptionUpgradeProvider>
  );
}
